import { createRoot, createSignal } from 'solid-js';
import { deck } from '~/lib/deck';
import { heat } from '~/lib/heat';
import { player } from '~/lib/player';
import { toolPrice } from '~/lib/shop';
import type { HackBand } from '~/lib/resolution';
import type { Tool } from '~/types/game';

/**
 * Economy: the ¤ sinks (buying tools, heat reduction) and the per-node dual
 * consequence state (a price multiplier that rises on a successful dual-hack
 * and decays back, and a lockout set on a failed dual-hack). Module-level
 * singleton; per-node state is plain data, persistence-ready (step 6).
 *
 * ALL spends here are earned-¤ only — never wire any of this to real money.
 */

// ── Heat-reduction service ─────────────────────────────────────────────────
/** Heat cleared per purchase (a meaningful chunk, NOT a full wipe — preserves
 *  the "pay to cool now vs walk it off free" tension). */
export const HEAT_REDUCTION_CHUNK = 30;
/** ¤ per heat point cleared. */
export const HEAT_REDUCTION_COST_PER_POINT = 8;

// ── Graduated dual-node consequence ────────────────────────────────────────
/** Each successful dual-hack adds this to the node's price multiplier… */
export const DUAL_PRICE_BUMP = 0.5;
/** …capped here. */
export const DUAL_PRICE_MULT_MAX = 3.0;
/** …and the multiplier decays linearly back to 1.0 over this window. */
export const DUAL_PRICE_DECAY_MS = 5 * 60 * 1000; // 5 min
/** A failed dual-hack locks the vendor out for this long. */
export const DUAL_LOCKOUT_MS = 3 * 60 * 1000; // 3 min

interface DualNodeState {
  multiplier: number; // multiplier value as last set (≥1)
  multiplierAt: number; // when it was last set (for decay)
  lockoutUntil: number; // epoch ms; transactions blocked until then
}

function createEconomy() {
  const [states, setStates] = createSignal<Record<string, DualNodeState>>({});

  /** The node's current price multiplier, decayed toward 1.0 by elapsed time. */
  const priceMultiplier = (nodeId: string, now: number = Date.now()): number => {
    const s = states()[nodeId];
    if (!s || s.multiplier <= 1) return 1;
    const t = Math.max(0, 1 - (now - s.multiplierAt) / DUAL_PRICE_DECAY_MS);
    return 1 + (s.multiplier - 1) * t;
  };

  const isLockedOut = (nodeId: string, now: number = Date.now()): boolean => {
    const s = states()[nodeId];
    return !!s && now < s.lockoutUntil;
  };
  const lockoutRemainingMs = (nodeId: string, now: number = Date.now()): number => {
    const s = states()[nodeId];
    return s ? Math.max(0, s.lockoutUntil - now) : 0;
  };
  /** Raw lockout expiry (epoch ms) for a node, or 0 if none. Read-only; used by
   *  the DIAG economy logger to record the absolute cooldown expiry. */
  const lockoutUntil = (nodeId: string): number => states()[nodeId]?.lockoutUntil ?? 0;

  /** Record a dual-node hack outcome: success raises the price multiplier (you
   *  annoyed them); failure locks the vendor out (you burned them). */
  const onDualHack = (nodeId: string, band: HackBand): void => {
    const now = Date.now();
    setStates((m) => {
      const prev = m[nodeId] ?? { multiplier: 1, multiplierAt: now, lockoutUntil: 0 };
      if (band === 'clean' || band === 'partial') {
        // Stack on the already-decayed current multiplier so repeat hacks build.
        const current = priceMultiplier(nodeId, now);
        return {
          ...m,
          [nodeId]: {
            multiplier: Math.min(DUAL_PRICE_MULT_MAX, current + DUAL_PRICE_BUMP),
            multiplierAt: now,
            lockoutUntil: prev.lockoutUntil,
          },
        };
      }
      return { ...m, [nodeId]: { ...prev, lockoutUntil: now + DUAL_LOCKOUT_MS } };
    });
  };

  /** The ¤ price to buy `tool` at node `nodeId` (base × the node's multiplier).
   *  Pure-ish read; the multiplier is 1 for pure shops with no dual state. */
  const priceOf = (nodeId: string, tool: Tool): number =>
    Math.round(toolPrice(tool) * priceMultiplier(nodeId));

  /** Buy a tool at a shop/dual node. Fails (no-op) if out of range, locked out,
   *  already owned, or unaffordable. Debits ¤ and adds the tool to the deck.
   *  `inRange` is the shared node-view in-range flag (same source/constant as
   *  the hack gate) — defensively enforced here, not just in the UI: viewing a
   *  vendor is ungated, transacting requires being within ENGAGEMENT_RANGE_M. */
  const buyTool = (nodeId: string, tool: Tool, inRange: boolean): boolean => {
    if (!inRange || isLockedOut(nodeId) || deck.isOwned(tool.id)) return false;
    if (!player.spend(priceOf(nodeId, tool))) return false;
    deck.acquireTool(tool.id);
    return true;
  };

  /** Cost to reduce heat right now at node `nodeId` (chunk × per-point × mult). */
  const heatReductionCost = (nodeId: string): number => {
    const chunk = Math.min(HEAT_REDUCTION_CHUNK, heat.value());
    return Math.ceil(chunk * HEAT_REDUCTION_COST_PER_POINT * priceMultiplier(nodeId));
  };

  /** Buy heat reduction at a service/dual node. Clears a chunk (capped to
   *  current heat) for ¤. Fails if out of range, locked out, nothing to clear,
   *  or unaffordable. `inRange` is range-gated like buyTool (see above). */
  const buyHeatReduction = (nodeId: string, inRange: boolean): boolean => {
    if (!inRange || isLockedOut(nodeId)) return false;
    const chunk = Math.min(HEAT_REDUCTION_CHUNK, heat.value());
    if (chunk <= 0) return false;
    if (!player.spend(heatReductionCost(nodeId))) return false;
    heat.reduce(chunk);
    return true;
  };

  return {
    states,
    priceMultiplier,
    isLockedOut,
    lockoutRemainingMs,
    lockoutUntil,
    onDualHack,
    priceOf,
    buyTool,
    heatReductionCost,
    buyHeatReduction,
  };
}

export const economy = createRoot(() => createEconomy());
